import { UnifiedFieldIcon } from '@modules/common/components/UnifiedFieldIcon';
import {
  useIsSearchToFeedEnabled,
  useIsUnifiedFieldsEnabled,
} from '@modules/common/hooks';
import { formatNumToCompactNotation } from '@modules/common/utils';
import { ManageMetadataStarIconButton } from '@modules/integrationsV2/components/ManageMetadataSection/ManageMetadataStarIconButton';
import { ManageMetadataVisibilityToggleButton } from '@modules/integrationsV2/components/ManageMetadataSection/ManageMetadataVisibilityToggleButton';
import {
  IntegrationSource,
  ManageMetadataTableRow,
} from '@modules/integrationsV2/types';
import { getDisplayNameForMetadataType } from '@modules/integrationsV2/utils';
import { EditRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { IntegrationType } from 'generated/graphql';
import pluralize from 'pluralize';
import { useMemo } from 'react';
import { ManageMetadataTableActions } from '../components/ManageMetadataSection/ManageMetadataTableActions';

interface GetManageMetadataTableColumnsProps {
  onEditButtonClick: (metadata: ManageMetadataTableRow) => void;
  shouldShowStarredColumn?: boolean;
  onEditUnifiedFieldButtonClick?: (metadataKey?: string) => void;
  integrationSource: IntegrationSource;
}

export const useGetManageMetadataTableColumns = ({
  onEditButtonClick,
  shouldShowStarredColumn = true,
  onEditUnifiedFieldButtonClick,
  integrationSource,
}: GetManageMetadataTableColumnsProps): GridColDef<ManageMetadataTableRow>[] => {
  const isUnifiedFieldsEnabled = useIsUnifiedFieldsEnabled();

  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  return useMemo(() => {
    const { channel, namespace, integrationType, source } = integrationSource;

    const columns: GridColDef<ManageMetadataTableRow>[] = [
      {
        field: 'isImportant',
        headerName: 'Starred',
        headerAlign: 'center',
        type: 'boolean',
        minWidth: 90,
        renderCell: (params) => {
          return (
            <ManageMetadataStarIconButton
              metadata={params.row}
              eqlSource={source ?? ''}
            />
          );
        },
        align: 'center',
      },
      {
        field: 'displayText',
        minWidth: 220,
        headerName: 'Name',
        flex: 1,
        renderCell: (params) => (
          <>
            <Stack
              direction="column"
              sx={{
                overflow: 'hidden',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                lineHeight: '16px',
                py: 0.5,
              }}
            >
              <Box
                sx={{
                  whiteSpace: 'wrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.row.displayText}
              </Box>
              <Box>
                {isUnifiedFieldsEnabled && (
                  <Typography variant="caption" whiteSpace="nowrap">
                    {params.row.referencedIn?.displayText && (
                      <>
                        Part of{' '}
                        <UnifiedFieldIcon
                          fill="currentColor"
                          sx={{
                            fontSize: '1rem',
                          }}
                        />{' '}
                        Unified Field:
                        <Button
                          variant="text"
                          color="primary"
                          size="small"
                          endIcon={
                            <EditRounded
                              sx={{
                                fontSize: '0.8rem !important',
                              }}
                            />
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditUnifiedFieldButtonClick?.(
                              params.row.referencedIn?.key,
                            );
                          }}
                          sx={{
                            fontSize: '0.75rem',
                          }}
                        >
                          {params.row.referencedIn?.displayText}
                        </Button>
                      </>
                    )}
                  </Typography>
                )}
              </Box>
            </Stack>
          </>
        ),
      },
      {
        field: 'originalDisplayText',
        width: 200,
        headerName: 'Original Name',
      },
      {
        field: 'type',
        headerName: 'Data Type',
        minWidth: 100,
        renderCell: (params) => {
          return getDisplayNameForMetadataType(params.row.type);
        },
      },
      {
        field: 'cardinality',
        minWidth: 120,
        type: 'number',
        headerName: 'Unique Values',
        renderCell: (params) => {
          return formatNumToCompactNotation({
            value: params.value,
            maximumFractionDigits: 1,
          });
        },
      },
      {
        field: 'coverage',
        headerName: 'Coverage',
        minWidth: 100,
        type: 'number',
        sortable: false,
        renderCell: (params) => {
          const coverage = params.value;

          return coverage !== undefined ? (
            `${coverage}%`
          ) : (
            <Skeleton
              variant="rounded"
              width={30}
              height={16}
              sx={{
                display: 'inline-block',
                verticalAlign: 'middle',
                bgcolor: 'text.disabled',
              }}
            />
          );
        },
        renderHeader: () => {
          const integrationTypeLabel =
            integrationType === IntegrationType.Feedback
              ? 'feedback records'
              : pluralize(integrationType.toLowerCase());

          const sourceNameLabel = namespace
            ? `${namespace} - ${channel.displayName}`
            : channel.displayName;

          return (
            <Tooltip
              title={`Percentage of ${integrationTypeLabel} from ${sourceNameLabel} that have the populated metadata value.`}
              placement="top"
              arrow
            >
              <Typography variant="subtitle2">Coverage</Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'isHidden',
        headerName: 'Visibility',
        type: 'boolean',
        minWidth: 130,
        flex: 0.5,
        renderCell: (params) => (
          <>
            <ManageMetadataVisibilityToggleButton metadata={params.row} />
            <ManageMetadataTableActions
              onEditButtonClick={onEditButtonClick}
              metadata={params.row}
            />
          </>
        ),
        renderHeader: () => (
          <Tooltip
            title={`Shows this metadata in the platform (in ${
              isSearchToFeedEnabled ? 'Feed' : 'Search'
            }, Quantify, Taxonomy, etc.)`}
            placement="top"
            arrow
          >
            <Typography variant="subtitle2">Visibility</Typography>
          </Tooltip>
        ),
      },
    ];

    if (!shouldShowStarredColumn) {
      return columns.filter((column) => column.field !== 'isImportant');
    }

    return columns;
  }, [
    integrationSource,
    isSearchToFeedEnabled,
    isUnifiedFieldsEnabled,
    onEditButtonClick,
    onEditUnifiedFieldButtonClick,
    shouldShowStarredColumn,
  ]);
};
